import { Injectable } from '@angular/core';
import { UrlEnum } from '../../enum/url.enum';
import { TagCsrModel } from '../../model/csr/tag-csr.model';
import { map, Observable } from 'rxjs';
import { HttpRequestService } from '../http-request/http-request.service';
import { UtilitiesService } from '../utilities/utilities.service';
import { ExternalCsrCampaignDonationModel } from '../../model/csr/external-csr-donation.model';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { PledgePaymentModel } from '../../model/pledge-donation.model';
import {
  CorporateInternalDonationConfirmModel,
  CorporateInternalDonationModel,
} from '../../model/corporate/corporate-donation.model';

@Injectable({
  providedIn: 'root',
})
export class TagCsrService {
  private tag_csr_ngo = `${UrlEnum.SHOHAY_CORPORATE}/allowed-csr-ngo-campaign`;
  private tag_csr_campaign_donation = `${UrlEnum.SHOHAY_CORPORATE}/external-csr`;
  private company = `${UrlEnum.SHOHAY_CORPORATE}/company-profile`;
  private mapTagCsrNgo = `${UrlEnum.SHOHAY_CORPORATE}/csr-ngo-campaign-mapping`;
  private corporatePledging = `${UrlEnum.SHOHAY_CORPORATE}/corporate-pledging`;

  constructor(
    private readonly httpRequestService: HttpRequestService,
    private readonly utilitiesService: UtilitiesService,
    private readonly localStorageService: LocalStorageService
  ) {}

  public tagCampaignNgo(body: TagCsrModel): Observable<any> {
    return this.httpRequestService
      .post(`${this.tag_csr_ngo}/create`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getAllTaggedNgoCampaigns(): Observable<any> {
    return this.httpRequestService.get(`${this.tag_csr_ngo}/get-all`).pipe(
      map((x: any) => this.utilitiesService.responseHandler(x)),
      map((x: any) => x.sort((a, b) => a.ngo_name.localeCompare(b.ngo_name))),
      map((x: any[]) =>
        x.map((ngo: any) => ({
          ...ngo,
          logo_url: this.utilitiesService.jsonParse(ngo?.logo_url),
          cover_img_vdo: this.utilitiesService.jsonParse(ngo?.cover_img_vdo),
        }))
      )
    );
  }

  public tagCsrCampaignDonation(
    body: ExternalCsrCampaignDonationModel
  ): Observable<any> {
    return this.httpRequestService
      .post(
        `${this.tag_csr_campaign_donation}/pledging/create-campaign-donation`,
        body
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public tagCsrNgoDonation(
    body: ExternalCsrCampaignDonationModel
  ): Observable<any> {
    return this.httpRequestService
      .post(
        `${this.tag_csr_campaign_donation}/pledging/create-direct-donation`,
        body
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getTaggedExternalCsrDonation(
    start_date: Date,
    end_date: Date,
    company_id: string
  ): Observable<any> {
    start_date.setDate(start_date.getDate() + 1);
    end_date.setDate(end_date.getDate() + 1);

    return this.httpRequestService
      .get(
        `${this.tag_csr_campaign_donation}/pledging/get-donation-pledging?company_id=${company_id}&start_date=${start_date}&end_date=${end_date}&status=ALL`
      )
      .pipe(
        map((response: any) => this.utilitiesService.responseHandler(response))
      );
  }

  public getAllCompany(): Observable<any> {
    return this.httpRequestService
      .get(`${this.company}/get-all-company`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getCsrMap(): Observable<any> {
    return this.httpRequestService
      .get(
        `${this.mapTagCsrNgo}/get-by-company/${
          this.localStorageService.getSessionUser().company_id
        }`
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public postCsrMap(body: any): Observable<any> {
    return this.httpRequestService
      .post(`${this.mapTagCsrNgo}/create`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public updateStatusDonation(
    id: string,
    body: PledgePaymentModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(
        `${this.tag_csr_campaign_donation}/pledging/confirm-pledging/${id}`,
        body
      )
      .pipe(
        map((response: any) => this.utilitiesService.responseHandler(response))
      );
  }

  public untagCsrNgoNpo(id: any): Observable<any> {
    return this.httpRequestService
      .patch(`${this.tag_csr_ngo}/untag/${id}`, {})
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getInternalCsrDonation(
    start_date: Date,
    end_date: Date,
    company_id: string
  ): Observable<any> {
    start_date.setDate(start_date.getDate() + 1);
    end_date.setDate(end_date.getDate() + 1);

    return this.httpRequestService
      .get(
        `${
          this.corporatePledging
        }/get-donation-pledging?company_id=${company_id}&start_date=${start_date.toISOString()}&end_date=${end_date.toISOString()}&status=ALL`
      )
      .pipe(
        map((response: any) => this.utilitiesService.responseHandler(response))
      );
  }

  public csrInternalCampaignDonation(
    body: CorporateInternalDonationModel
  ): Observable<any> {
    return this.httpRequestService
      .post(`${this.corporatePledging}/create`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public csrInternalCampaignConfirmDonation(
    id: string,
    body: CorporateInternalDonationConfirmModel
  ): Observable<any> {
    return this.httpRequestService
      .patch(`${this.corporatePledging}/confirm-pledging/${id}`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }
}
